import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import ManagerLayout from './ManagerLayout';
import {
  Search, RefreshCw, ExternalLink,
  Building2, CheckCircle, Clock, AlertCircle
} from 'lucide-react';

const PLAN_CONFIG = {
  trial: { color: '#94A3B8', bg: '#F1F5F9', label: 'Trial' },
  basic: { color: '#4F8EF7', bg: '#EFF6FF', label: 'Basic' },
  pro: { color: '#7C3AED', bg: '#F5F3FF', label: 'Pro' },
  enterprise: { color: '#F59E0B', bg: '#FEF3C7', label: 'Enterprise' },
};

export default function ManagerCompanies() {
  const [manager, setManager] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [companyTickets, setCompanyTickets] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { data: mgr } = await supabase
        .from('managers').select('*')
        .eq('auth_user_id', user.id).maybeSingle();

      if (!mgr) { setLoading(false); return; }
      setManager(mgr);

      // Get assigned companies with full details
      const { data: assigns } = await supabase
        .from('company_manager_assignments')
        .select(`
          id, assigned_at,
          companies(
            id, name, contact_email, contact_name,
            city, country, industry, company_size,
            verification_status, is_verified, is_banned,
            website, logo_url, created_at
          )
        `)
        .eq('manager_id', mgr.id)
        .eq('is_active', true)
        .order('assigned_at', { ascending: false });

      const companyList = (assigns || [])
        .map(a => ({ ...a.companies, assigned_at: a.assigned_at }))
        .filter(Boolean);

      setCompanies(companyList);

      // Get all tickets for this manager
      const { data: ticketsData } = await supabase
        .from('support_tickets').select('*')
        .eq('manager_id', mgr.id)
        .order('created_at', { ascending: false });

      setTickets(ticketsData || []);
    } catch (err) {
      console.error('ManagerCompanies load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCompany = (company) => {
    setSelected(company);
    const ct = tickets.filter(t => t.company_id === company.id);
    setCompanyTickets(ct);
  };

  const filtered = companies.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.contact_email?.toLowerCase().includes(q) ||
      c.city?.toLowerCase().includes(q) ||
      c.industry?.toLowerCase().includes(q)
    );
  });

  // Stats
  const openTicketCount = tickets.filter(t =>
    ['open', 'in_progress'].includes(t.status)).length;
  const urgentCount = tickets.filter(t =>
    t.priority === 'urgent' && t.status !== 'resolved').length;

  return (
    <ManagerLayout manager={{ ...manager, current_companies: companies.length }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1E293B' }}>
          My Companies
        </h1>
        <p style={{ color: '#94A3B8', fontSize: 13, marginTop: 4 }}>
          {companies.length} companies assigned to you ·{' '}
          {manager?.max_companies - companies.length} slots remaining
        </p>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
        gap: 12, marginBottom: 24,
      }}>
        {[
          { l: 'Total Companies', v: companies.length, max: manager?.max_companies, i: '🏢', c: '#4F8EF7' },
          { l: 'Total Tickets', v: tickets.length, i: '🎫', c: '#7C3AED' },
          { l: 'Open Tickets', v: openTicketCount, i: '🔔', c: '#F59E0B' },
          { l: 'Urgent', v: urgentCount, i: '🚨', c: '#EF4444' },
        ].map((s, i) => (
          <div key={i} style={{
            background: '#fff', borderRadius: 14, padding: '16px 18px',
            border: '1px solid #E2E8F0',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 24 }}>{s.i}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.c }}>
                {s.v}{s.max ? `/${s.max}` : ''}
              </div>
              <div style={{ fontSize: 10, color: '#94A3B8' }}>{s.l}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 290px)' }}>

        {/* Left: Company list */}
        <div style={{
          width: 360, flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{
              position: 'absolute', left: 10,
              top: '50%', transform: 'translateY(-50%)', color: '#94A3B8',
            }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search companies..."
              style={{
                width: '100%', padding: '9px 12px 9px 32px',
                border: '1.5px solid #E2E8F0', borderRadius: 10,
                fontSize: 13, outline: 'none', background: '#fff',
              }} />
          </div>

          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: 12, color: '#94A3B8' }}>
              {filtered.length} companies
            </span>
            <button onClick={load} style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '5px 10px', borderRadius: 8,
              border: '1px solid #E2E8F0', background: '#fff',
              fontSize: 12, cursor: 'pointer', color: '#64748B',
            }}>
              <RefreshCw size={12} />
            </button>
          </div>

          {/* List */}
          <div style={{
            flex: 1, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>
                Loading...
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8', fontSize: 13 }}>
                <Building2 size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
                <div>No companies assigned yet</div>
              </div>
            ) : filtered.map(c => {
              const isSelected = selected?.id === c.id;
              const companyTicketCount = tickets.filter(t => t.company_id === c.id);
              const hasUrgent = companyTicketCount.some(t =>
                t.priority === 'urgent' && t.status !== 'resolved');
              const openCount = companyTicketCount.filter(t =>
                ['open', 'in_progress'].includes(t.status)).length;

              return (
                <div key={c.id} onClick={() => openCompany(c)} style={{
                  background: isSelected
                    ? 'linear-gradient(135deg,rgba(79,142,247,0.07),rgba(124,58,237,0.04))'
                    : '#fff',
                  borderRadius: 12, padding: 14, cursor: 'pointer',
                  border: isSelected
                    ? '1.5px solid rgba(79,142,247,0.35)'
                    : hasUrgent
                      ? '1.5px solid rgba(239,68,68,0.3)'
                      : '1.5px solid #E2E8F0',
                  transition: 'all 0.15s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Avatar */}
                    <div style={{
                      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                      background: c.logo_url
                        ? 'transparent'
                        : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 800, fontSize: 15,
                      overflow: 'hidden',
                    }}>
                      {c.logo_url
                        ? <img src={c.logo_url} alt={c.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : c.name?.[0]?.toUpperCase()}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          fontWeight: 700, fontSize: 13, color: '#1E293B',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {c.name}
                        </div>
                        {hasUrgent && (
                          <span style={{ fontSize: 9, color: '#EF4444', flexShrink: 0 }}>
                            🚨 URGENT
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: 11, color: '#94A3B8',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {c.industry} · {c.city}, {c.country}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      {/* Verification status */}
                      <div style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: c.is_verified ? '#10B981' : '#F59E0B',
                      }} />
                      {openCount > 0 && (
                        <span style={{
                          fontSize: 9, fontWeight: 800, padding: '2px 6px',
                          borderRadius: 10, background: '#FEF3C7', color: '#F59E0B',
                        }}>
                          {openCount} ticket{openCount > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ fontSize: 10, color: '#CBD5E1', marginTop: 6 }}>
                    Assigned {new Date(c.assigned_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Company detail */}
        <div style={{
          flex: 1, background: '#fff', borderRadius: 16,
          border: '1px solid #E2E8F0', overflowY: 'auto',
        }}>
          {!selected ? (
            <div style={{
              height: '100%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              color: '#94A3B8', gap: 12,
            }}>
              <Building2 size={48} strokeWidth={1.5} />
              <div style={{ fontSize: 15, fontWeight: 600, color: '#64748B' }}>
                Select a company to view details
              </div>
            </div>
          ) : (
            <div style={{ padding: 28 }}>
              {/* Company header */}
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 16,
                marginBottom: 24, paddingBottom: 20,
                borderBottom: '1px solid #F1F5F9',
              }}>
                <div style={{
                  width: 64, height: 64, borderRadius: 16, flexShrink: 0,
                  background: selected.logo_url
                    ? 'transparent' : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 900, fontSize: 24,
                  overflow: 'hidden', border: '1px solid #E2E8F0',
                }}>
                  {selected.logo_url
                    ? <img src={selected.logo_url} alt={selected.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : selected.name?.[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1E293B', margin: 0 }}>
                      {selected.name}
                    </h2>
                    <span style={{
                      padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                      background: selected.is_verified ? '#D1FAE5' : '#FEF3C7',
                      color: selected.is_verified ? '#10B981' : '#F59E0B',
                    }}>
                      {selected.is_verified ? '✓ Verified' : 'Pending'}
                    </span>
                    {selected.is_banned && (
                      <span style={{
                        padding: '3px 8px', borderRadius: 5, fontSize: 11,
                        fontWeight: 700, background: '#FFE4E6', color: '#EF4444',
                      }}>
                        🚫 Banned
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#94A3B8', marginTop: 4 }}>
                    {selected.industry} · {selected.city}, {selected.country}
                  </div>
                  {selected.website && (
                    <a href={selected.website} target="_blank" rel="noreferrer"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 12, color: '#4F8EF7', marginTop: 4,
                        textDecoration: 'none',
                      }}>
                      <ExternalLink size={11} />{selected.website}
                    </a>
                  )}
                </div>
              </div>

              {/* Contact info */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: 12, marginBottom: 24,
              }}>
                {[
                  ['Contact Person', selected.contact_name],
                  ['Email', selected.contact_email],
                  ['Industry', selected.industry],
                  ['Company Size', selected.company_size],
                  ['City', selected.city],
                  ['Country', selected.country],
                  ['Joined', new Date(selected.created_at).toLocaleDateString('en-US', {
                    month: 'long', day: 'numeric', year: 'numeric' })],
                  ['Assigned', new Date(selected.assigned_at).toLocaleDateString('en-US', {
                    month: 'long', day: 'numeric', year: 'numeric' })],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} style={{
                    background: '#F8FAFF', borderRadius: 10,
                    padding: '10px 14px', border: '1px solid #E2E8F0',
                  }}>
                    <div style={{
                      fontSize: 10, color: '#94A3B8',
                      fontWeight: 700, marginBottom: 3,
                    }}>
                      {k.toUpperCase()}
                    </div>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: '#1E293B',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {v}
                    </div>
                  </div>
                ))}
              </div>

              {/* Company tickets */}
              <div>
                <div style={{
                  fontWeight: 700, fontSize: 15, color: '#1E293B', marginBottom: 14,
                  display: 'flex', justifyContent: 'space-between',
                }}>
                  <span>Support Tickets ({companyTickets.length})</span>
                  {companyTickets.some(t => t.priority === 'urgent' &&
                      t.status !== 'resolved') && (
                    <span style={{ fontSize: 12, color: '#EF4444', fontWeight: 700 }}>
                      🚨 Has urgent tickets
                    </span>
                  )}
                </div>

                {companyTickets.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: '24px',
                    background: '#F8FAFF', borderRadius: 12,
                    color: '#94A3B8', fontSize: 13,
                  }}>
                    No tickets from this company yet
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {companyTickets.map(t => {
                      const statusColors = {
                        open: '#4F8EF7', in_progress: '#7C3AED',
                        waiting_company: '#F59E0B', resolved: '#10B981', closed: '#94A3B8',
                      };
                      const statusLabels = {
                        open: 'Open', in_progress: 'In Progress',
                        waiting_company: 'Awaiting Reply', resolved: 'Resolved',
                        closed: 'Closed',
                      };
                      const sc = statusColors[t.status] || '#94A3B8';
                      return (
                        <div key={t.id} style={{
                          background: '#F8FAFF', borderRadius: 10, padding: '12px 14px',
                          border: '1px solid #E2E8F0',
                          borderLeft: t.priority === 'urgent' ? '4px solid #EF4444' : undefined,
                        }}>
                          <div style={{
                            display: 'flex', justifyContent: 'space-between',
                            alignItems: 'flex-start', gap: 10,
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: 700, fontSize: 13, color: '#1E293B',
                              }}>
                                {t.title}
                              </div>
                              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                                {new Date(t.created_at).toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric', year: 'numeric',
                                })}
                                {t.priority === 'urgent' && (
                                  <span style={{ color: '#EF4444', fontWeight: 700 }}>
                                    {' '}· 🚨 URGENT
                                  </span>
                                )}
                              </div>
                            </div>
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '3px 8px',
                              borderRadius: 5, background: `${sc}15`, color: sc,
                              flexShrink: 0,
                            }}>
                              {statusLabels[t.status] || t.status}
                            </span>
                          </div>
                          {t.manager_response && (
                            <div style={{
                              marginTop: 8, padding: '8px 12px',
                              background: 'rgba(79,142,247,0.06)',
                              borderRadius: 8,
                              fontSize: 12, color: '#475569', lineHeight: 1.5,
                            }}>
                              <span style={{
                                fontSize: 10, fontWeight: 700, color: '#4F8EF7',
                              }}>
                                Your response:{' '}
                              </span>
                              {t.manager_response}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </ManagerLayout>
  );
}